"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Home, RefreshCw, ArrowLeft, Copy, Check } from "lucide-react";
import { logger } from '@/lib/logger';

export default function DashboardError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string>("");

  useEffect(() => {
    // Build comprehensive error details for debugging
    const details = {
      message: error.message || "(no message)",
      name: error.name || "(no name)",
      digest: error.digest || "(no digest)",
      stack: error.stack || "(no stack)",
      url: typeof window !== "undefined" ? window.location.href : "(unknown)",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "(unknown)",
      timestamp: new Date().toISOString(),
    };

    // Log the full error details to console
    logger.error("Dashboard error details:", { error: details });

    // Store for display
    setErrorDetails(JSON.stringify(details, null, 2));

    // Report error to API for persistent logging
    fetch("/api/errors/report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(details),
    }).catch(() => {
      // Ignore reporting failures
    });
  }, [error]);

  const copyErrorDetails = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that don't support clipboard
      logger.info("Error details:", { errorDetails });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="pt-10 pb-8">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Icon */}
            <div className="rounded-full bg-warning/10 dark:bg-orange-900/20 p-5">
              <AlertTriangle className="h-12 w-12 text-warning dark:text-orange-400" />
            </div>

            {/* Error Message */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">
                Something Went Wrong
              </h2>
              <p className="text-base text-gray-700 dark:text-gray-300">
                We encountered an error while loading this page
              </p>
              {process.env.NODE_ENV === "development" && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Development Error Details:
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 p-3 rounded-md max-w-md overflow-auto text-left">
                    {error.message}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-400 max-w-md">
              This error has been logged. You can try reloading the page or
              navigate back to your dashboard.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
              <Button
                onClick={reset}
                className="flex-1"
                size="lg"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Link href="/dashboard" className="flex-1">
                <Button variant="outline" className="w-full" size="lg">
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            </div>

            {/* Quick Links */}
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700 w-full">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Or navigate to:
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Link href="/jobs">
                  <Button variant="ghost" size="sm">
                    Jobs
                  </Button>
                </Link>
                <Link href="/clients">
                  <Button variant="ghost" size="sm">
                    Clients
                  </Button>
                </Link>
                <Link href="/schedule">
                  <Button variant="ghost" size="sm">
                    Schedule
                  </Button>
                </Link>
                <Link href="/invoices">
                  <Button variant="ghost" size="sm">
                    Invoices
                  </Button>
                </Link>
              </div>
            </div>

            {/* Error Details for Support */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 w-full">
              {error.digest && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Error ID: {error.digest}
                </p>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={copyErrorDetails}
                className="text-xs"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Error Details for Support
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
